import React, { useEffect, useRef, useState } from 'react';
import { FaChevronRight, FaChevronLeft, FaCheck } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const WorkflowTabView = ({ 
  steps = [], 
  currentStep: initialStep = 1, 
  onStepChange,
  onComplete,
  children,
  maxWidth = 'max-w-7xl',
  previewOnly = false
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const headerRef = useRef(null);
  const didMountRef = useRef(false);
  const previousStepRef = useRef(initialStep);

  // 🔹 przewiń do początku komponentu przy zmianie kroku
  useEffect(() => {
    // Pomijaj przewijanie przy pierwszych renderach (także StrictMode) i przewijaj tylko, gdy krok się zmienił
    const isFirstRender = previousStepRef.current === currentStep && !didMountRef.current;
    if (isFirstRender) {
      didMountRef.current = true;
      return;
    }

    const hasStepChanged = previousStepRef.current !== currentStep;
    previousStepRef.current = currentStep;
    if (!hasStepChanged) return;

    if (headerRef.current) {
      headerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep]);

  const isActive = (stepId) => stepId === currentStep;
  const isCompleted = (stepId) => stepId < currentStep;
  const isClickable = (stepId) => true;

  const handleStepClick = (stepId) => {
    if (isClickable(stepId)) {
      setCurrentStep(stepId);
      if (onStepChange) {
        onStepChange(stepId);
      }
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      if (onStepChange) {
        onStepChange(nextStep);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      if (onStepChange) {
        onStepChange(prevStep);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div ref={headerRef} className={`w-full ${maxWidth} mx-auto p-6`}>
        {/* Progress Steps */}
        <div className="flex items-center justify-between relative">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            const active = isActive(step.id);
            const completed = isCompleted(step.id);
            const clickable = isClickable(step.id);

            return (
              <React.Fragment key={step.id}>
                {/* Step */}
                <div className="flex flex-col items-center relative z-10">
                  <button
                    onClick={() => handleStepClick(step.id)}
                    disabled={!clickable}
                    className={`flex flex-col items-center transition-all duration-200 ${
                      clickable ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed'
                    }`}
                  >
                    {/* Icon Circle */}
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-200 ${
                        active
                          ? 'bg-blue-500 text-white shadow-lg'
                          : completed
                          ? 'bg-green-500 text-white shadow-lg'
                          : 'bg-gray-100 border-2 border-gray-300 text-gray-400'
                      }`}
                    >
                      <IconComponent className="w-5 h-5" />
                    </div>

                    {/* Title */}
                    <h3
                      className={`font-semibold text-sm mb-1 transition-colors duration-200 ${
                        active
                          ? 'text-blue-500'
                          : completed
                          ? 'text-green-600'
                          : 'text-gray-700'
                      }`}
                    >
                      {step.title}
                    </h3>

                    {/* Description */}
                    <p className="text-xs text-gray-500 text-center max-w-32">
                      {step.description}
                    </p>
                  </button>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="flex-1 h-0.5 mx-4 relative">
                    <div
                      className={`h-full transition-all duration-300 ${
                        completed ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-8">
          <h2 className="text-xl font-semibold mb-4">
            {t('workflow.common.step')} {currentStep}: {steps.find(step => step.id === currentStep)?.title}
          </h2>
          <p className="text-gray-600 mb-6">
            {steps.find(step => step.id === currentStep)?.description}
          </p>
          
          {/* Content */}
          <div className="relative">
            <div 
              className="bg-gray-50 rounded-lg p-6"
              aria-disabled={previewOnly}
              {...(previewOnly ? { inert: '' } : {})}
            >
              {children}
            </div>
            {previewOnly && (
              <div className="absolute inset-0 rounded-lg bg-white/30 backdrop-blur-[1px] z-10">
              </div>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-4">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="flex items-center px-6 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm"
            >
              <FaChevronLeft className="mr-2 w-3 h-3" />
              {t('workflow.common.back')}
            </button>
          )}
          {onComplete && currentStep === steps.length ? (
            <button
              type="button"
              onClick={onComplete}
              className="flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200 font-medium shadow-sm ml-auto text-sm"
            >
              {t('workflow.common.complete')}
              <FaCheck className="ml-2 w-3 h-3" />
            </button>
          ) : currentStep < steps.length && (
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 font-medium shadow-sm ml-auto text-sm"
            >
              {t('workflow.common.next')}
              <FaChevronRight className="ml-2 w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowTabView;
